import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import shareIcon from '../../assets/listing/icon-share.svg';

import CustomListingFields from './CustomListingFields';
import FavoriteButton from './FavoriteButton';

import css from './SectionListingDetails.module.css';

/**
 * One disclosure row: a hairline-separated heading that toggles its panel.
 *
 * The trigger is wrapped in a heading so the panels are reachable from a screen
 * reader's heading list, which is how the page's long-form copy is navigated.
 */
const Accordion = props => {
  const { id, label, children, defaultOpen = false } = props;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const panelId = `${id}-panel`;

  return (
    <div className={css.accordion}>
      <h2 className={css.accordionHeading}>
        <button
          type="button"
          id={id}
          className={css.accordionTrigger}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => setIsOpen(open => !open)}
        >
          <span className={css.accordionLabel}>{label}</span>
          <span className={css.accordionMarker} aria-hidden="true">
            <span className={css.markerBar} />
            <span
              className={classNames(css.markerBar, css.markerBarVertical, {
                [css.markerBarHidden]: isOpen,
              })}
            />
          </span>
        </button>
      </h2>
      <div
        id={panelId}
        className={css.accordionPanel}
        role="region"
        aria-labelledby={id}
        hidden={!isOpen}
      >
        {children}
      </div>
    </div>
  );
};

/**
 * Left column below the headline: the lender's own description, the two
 * disclosure panels, and the share row.
 *
 * The Details panel holds the Console-defined listing fields, so it stays
 * accurate as fields are added or removed in Console. Shipping & return only
 * states the delivery options the listing actually carries.
 *
 * @component
 * @param {Object} props
 * @param {string} props.description listing description
 * @param {Object} props.publicData listing publicData
 * @param {Object} props.metadata listing metadata
 * @param {Array} props.listingFieldConfigs hosted listing field configs
 * @param {Object} props.categoryConfiguration hosted category configuration
 * @param {string} props.title listing title, used when sharing
 * @returns {JSX.Element}
 */
const SectionListingDetails = props => {
  const intl = useIntl();
  const [shareState, setShareState] = useState(null);
  const {
    description,
    publicData,
    metadata,
    listingFieldConfigs,
    categoryConfiguration,
    title,
    listingId,
  } = props;

  const handleShare = () => {
    const url = typeof window !== 'undefined' ? window.location.href : null;
    if (!url) {
      return;
    }
    if (navigator.share) {
      // A cancelled share sheet rejects; that is not an error worth reporting.
      navigator.share({ title, url }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => setShareState('copied'), () => setShareState('failed'));
    }
  };

  return (
    <div className={css.root}>
      {description ? <p className={css.description}>{description}</p> : null}

      <Accordion id="listingDetails" label={<FormattedMessage id="ListingPage.detailsTitle" />}>
        <CustomListingFields
          publicData={publicData}
          metadata={metadata}
          listingFieldConfigs={listingFieldConfigs}
          categoryConfiguration={categoryConfiguration}
          intl={intl}
          detailsHeading={null}
        />
      </Accordion>

      <Accordion
        id="listingShipping"
        label={<FormattedMessage id="ListingPage.shippingReturnTitle" />}
      >
        <ul className={css.deliveryList}>
          {publicData?.shippingEnabled ? (
            <li>
              <FormattedMessage id="ListingPage.shippingAvailable" />
            </li>
          ) : null}
          {publicData?.pickupEnabled ? (
            <li>
              <FormattedMessage id="ListingPage.pickupAvailable" />
            </li>
          ) : null}
          <li>
            <FormattedMessage id="ListingPage.returnNote" />
          </li>
        </ul>
      </Accordion>

      {/* Figma 443:2371 — "Share :" + heart + share, gap 10 / icons gap 8. */}
      <div className={css.share}>
        <span className={css.shareLabel}>
          <FormattedMessage id="ListingPage.shareLabel" />
        </span>
        <div className={css.shareActions}>
          {listingId ? <FavoriteButton listingId={listingId} /> : null}
          <button type="button" className={css.shareButton} onClick={handleShare}>
            <span className={css.shareIconFrame}>
              <img
                className={css.shareIcon}
                src={shareIcon}
                alt=""
                width={15}
                height={15}
                aria-hidden="true"
              />
            </span>
            <span className={css.visuallyHidden}>
              <FormattedMessage id="ListingPage.shareAction" />
            </span>
          </button>
        </div>
        <span className={css.shareStatus} role="status">
          {shareState === 'copied' ? <FormattedMessage id="ListingPage.shareCopied" /> : null}
        </span>
      </div>
    </div>
  );
};

export default SectionListingDetails;
